import React, { useEffect, useMemo, useState } from 'react'

import { debounce } from 'lodash'

import { SxProps } from '@mui/material'

import InputAdornment from 'components_new/atoms/InputAdornment'
import TextField from 'components_new/atoms/TextField'

interface Props {
  endAdornment?: string | null
  error?: boolean
  helperText?: string
  initialValue: string
  onDebouncedChange: (value: string) => void
  sx?: SxProps
}

const RowEditor = React.memo((props: Props) => {
  const {
    endAdornment,
    error,
    helperText,
    initialValue,
    onDebouncedChange,
    sx
  } = props

  const [localValue, setLocalValue] = useState(initialValue)

  // memoize the debounced callback so it isn't recreated every render
  const debouncedChange = useMemo(
    () => debounce((value: string) => onDebouncedChange(value), 300),
    [onDebouncedChange]
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value

    setLocalValue(newValue)
    debouncedChange(newValue)
  }

  // cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedChange.cancel()
    }
  }, [debouncedChange])

  // success
  return (
    <TextField
      error={error}
      helperText={helperText}
      noGutter={true}
      onChange={handleChange}
      size="small"
      sx={sx}
      value={localValue}
      InputProps={
        endAdornment
          ? {
              endAdornment: (
                <InputAdornment position="end">{endAdornment}</InputAdornment>
              )
            }
          : undefined
      }
    />
  )
})

export default RowEditor
